import { getFeatureDefinition } from '../../../views/create-location-wizard/featureRegistry';
import type { LocationFeatureDetails, LocationFeatureDetailsByName } from '../../../types/Location';

/** Read-only summary of a feature's sub-fields, as shown to buyers under the feature label. */
export type FeatureSubDetail =
  | { kind: 'text'; text: string }
  | { kind: 'chips'; items: string[] }
  | { kind: 'groups'; groups: { label: string; items: string[] }[] };

type DetailValue = LocationFeatureDetails[string] | null | undefined;

const SEPARATOR = ' · ';

const asText = (value: DetailValue): string | undefined => {
  if (typeof value === 'string') return value.trim() || undefined;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
};

const asBoolean = (value: DetailValue): boolean | undefined => (typeof value === 'boolean' ? value : undefined);

const yesNo = (value: boolean) => (value ? 'Yes' : 'No');

const counted = (count: string, singular: string, plural: string) => `${count} ${count === '1' ? singular : plural}`;

const joinParts = (parts: (string | undefined)[]): string | undefined =>
  parts.filter((part): part is string => !!part).join(SEPARATOR) || undefined;

const getOptions = (feature: string, key: string) => {
  const field = getFeatureDefinition(feature)?.subFields.find((f) => f.key === key);
  return field && (field.type === 'select' || field.type === 'checkboxGroup') ? field.options : [];
};

const getFieldLabel = (feature: string, key: string) =>
  getFeatureDefinition(feature)?.subFields.find((f) => f.key === key)?.label ?? key;

/** Registry label for a select value; unknown values fall back to the raw value. */
const optionLabel = (feature: string, key: string, value: DetailValue): string | undefined => {
  const text = asText(value);
  if (!text) return undefined;
  return getOptions(feature, key).find((option) => option.value === text)?.label ?? text;
};

/** Registry labels for a checkbox group, in registry order with unknown values last. */
const optionLabels = (feature: string, key: string, value: DetailValue): string[] => {
  if (!Array.isArray(value)) return [];
  const selected = value
    .filter((v): v is string => typeof v === 'string')
    .map((v) => v.trim())
    .filter(Boolean);
  const options = getOptions(feature, key);
  const known = options.filter((option) => selected.includes(option.value)).map((option) => option.label);
  const unknown = selected.filter((v) => !options.some((option) => option.value === v));
  return [...known, ...unknown];
};

const text = (value: string | undefined): FeatureSubDetail | null => (value ? { kind: 'text', text: value } : null);

const chips = (items: string[]): FeatureSubDetail | null => (items.length > 0 ? { kind: 'chips', items } : null);

/** Footage retention is free text: "30" or "30 days" become "30-day retention", anything else is shown as typed. */
const formatRetention = (value: DetailValue): string | undefined => {
  const retention = asText(value);
  if (!retention) return undefined;
  const days = /^(\d+)(\s*days?)?$/i.exec(retention);
  return days ? `${days[1]}-day retention` : `Retention: ${retention}`;
};

const formatCameras = (feature: string, details: LocationFeatureDetails) => {
  const count = optionLabel(feature, 'cameraCount', details.cameraCount);
  return text(joinParts([count && counted(count, 'camera', 'cameras'), formatRetention(details.footageRetentionDays)]));
};

type Formatter = (feature: string, details: LocationFeatureDetails) => FeatureSubDetail | null;

const FORMATTERS: Partial<Record<string, Formatter>> = {
  'Manned Security': (feature, details) => {
    const hours = asText(details.staffedSecurityHours);
    const guards = optionLabel(feature, 'securityGuardCount', details.securityGuardCount);
    const armed = asBoolean(details.armedGuards);
    return text(joinParts([
      hours && `Staffed ${hours}`,
      guards && counted(guards, 'guard', 'guards'),
      armed === undefined ? undefined : armed ? 'Armed' : 'Unarmed',
    ]));
  },
  'Fenced / Gated': (_feature, details) => {
    const height = asText(details.fencingHeightFeet);
    const barbedWire = asBoolean(details.barbedWire);
    return text(joinParts([
      height && `Height: ${height} ft`,
      barbedWire === undefined ? undefined : `Barbed wire: ${yesNo(barbedWire)}`,
    ]));
  },
  'Yard Cameras': formatCameras,
  'Gate Cameras': formatCameras,
  Lights: (_feature, details) => {
    const count = asText(details.lightCount);
    return text(count && /^\d+$/.test(count) ? `${counted(count, 'light', 'lights')} on-property` : undefined);
  },
  Paved: (feature, details) => {
    const surface = optionLabel(feature, 'surfaceMaterial', details.surfaceMaterial);
    return text(surface && `Surface: ${surface}`);
  },
  'Daily Inventory Report': (feature, details) => {
    const system = optionLabel(feature, 'inventoryReportingSystem', details.inventoryReportingSystem);
    return text(system && `System: ${system}`);
  },
  'Security Certifications': (feature, details) => chips(optionLabels(feature, 'certifications', details.certifications)),
  'Hazmat Accepted': (feature, details) => chips(optionLabels(feature, 'hazmatClasses', details.hazmatClasses)),
  Bonded: (_feature, details) => {
    const bonded = asBoolean(details.bonded);
    return text(bonded === undefined ? undefined : `Bonded: ${yesNo(bonded)}`);
  },
  'On-Site Equipment & Service': (feature, details) => {
    const groups = ['equipment', 'services']
      .map((key) => ({ label: getFieldLabel(feature, key), items: optionLabels(feature, key, details[key]) }))
      .filter((group) => group.items.length > 0);
    return groups.length > 0 ? { kind: 'groups', groups } : null;
  },
};

/**
 * Buyer-facing summary of a feature's sub-details, e.g. "4 cameras · 30-day retention".
 * Returns null when the feature has no sub-fields or the supplier left them all blank.
 */
export const getFeatureSubDetail = (
  feature: string,
  details: LocationFeatureDetails | null | undefined
): FeatureSubDetail | null => {
  const format = Object.prototype.hasOwnProperty.call(FORMATTERS, feature) ? FORMATTERS[feature] : undefined;
  return format && details ? format(feature, details) : null;
};

/** Features surfaced as trust signals on search cards, in display order. */
export const TRUST_SIGNAL_FEATURES = ['Security Certifications', 'Bonded', 'Hazmat Accepted', 'Manned Security'] as const;

export type TrustSignalFeature = (typeof TRUST_SIGNAL_FEATURES)[number];

export interface TrustSignal {
  key: string;
  feature: TrustSignalFeature;
  label: string;
}

/**
 * Trust-signal chips for a search card. Uses the plain feature list so they show before suppliers add
 * details; details only refine the label (e.g. one chip per certification, "Armed Security").
 */
export const getTrustSignals = (
  features: string[] | null | undefined,
  detailsByName: LocationFeatureDetailsByName | null | undefined
): TrustSignal[] => {
  const present = new Set(features ?? []);
  return TRUST_SIGNAL_FEATURES.filter((feature) => present.has(feature)).flatMap((feature): TrustSignal[] => {
    const details = detailsByName?.[feature] ?? {};
    switch (feature) {
      case 'Security Certifications': {
        const certifications = optionLabels(feature, 'certifications', details.certifications);
        return certifications.length > 0
          ? certifications.map((label) => ({ key: `${feature}-${label}`, feature, label }))
          : [{ key: feature, feature, label: 'Security Certified' }];
      }
      case 'Bonded':
        return asBoolean(details.bonded) === false ? [] : [{ key: feature, feature, label: 'Bonded' }];
      case 'Hazmat Accepted':
        return [{ key: feature, feature, label: 'Hazmat' }];
      case 'Manned Security':
        return [{ key: feature, feature, label: asBoolean(details.armedGuards) ? 'Armed Security' : 'Manned Security' }];
      default:
        return [];
    }
  });
};
